import React, { useEffect, useState } from 'react';
import { X, ChevronLeft, ChevronRight, Check, Sparkles } from 'lucide-react';
import { GUIDE_STEPS as STEPS } from './demoGuideSteps';

/* =========================================================
   Getting-started guide modal — fresh-road (new UI) styling.
   Step content lives in ./demoGuideSteps (shared with the
   full-page DemoCreateAI guide). Actions route via onNavigate.
   ========================================================= */

const DemoTutorialModal = ({ open, onClose, onNavigate }) => {
  const [index, setIndex] = useState(0);
  const total = STEPS.length;

  useEffect(() => {
    if (open) setIndex(0);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowRight') setIndex((i) => Math.min(total - 1, i + 1));
      else if (e.key === 'ArrowLeft') setIndex((i) => Math.max(0, i - 1));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose, total]);

  if (!open) return null;

  const step = STEPS[index];
  const StepIcon = step.icon;
  const canPrev = index > 0;
  const canNext = index < total - 1;

  const go = (route) => {
    onNavigate?.(route);
    onClose();
  };

  return (
    <div
      className="demo-ui fixed inset-0 z-[2000] flex items-center justify-center bg-slate-900/50 p-3 backdrop-blur-sm sm:p-4"
      role="dialog"
      aria-modal="true"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="flex h-[92vh] max-h-[720px] w-full max-w-[920px] flex-col overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-2xl">
        {/* Header */}
        <div className="flex shrink-0 items-start gap-4 bg-gradient-to-r from-teal-600 to-emerald-600 px-6 py-5 text-white">
          <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/20">
            <StepIcon size={24} />
          </span>
          <div className="min-w-0 flex-1">
            <p className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-white/85">
              <Sparkles size={12} /> Getting started
            </p>
            <h2 className="mt-0.5 text-[22px] font-bold leading-tight">{step.title}</h2>
            <p className="mt-0.5 text-[12.5px] text-white/85">Step {index + 1} of {total}</p>
          </div>
          <button onClick={onClose} className="shrink-0 rounded-xl p-2 text-white/90 hover:bg-white/20" aria-label="Close">
            <X size={22} />
          </button>
        </div>

        {/* Stepper */}
        <div className="flex shrink-0 items-start justify-between gap-0 border-b border-slate-100 bg-slate-50/70 px-4 py-4">
          {STEPS.map((s, i) => {
            const done = i < index;
            const active = i === index;
            return (
              <React.Fragment key={s.title}>
                {i > 0 && <div className={`mt-4 h-[3px] flex-1 rounded-full ${i <= index ? 'bg-teal-600' : 'bg-slate-200'}`} />}
                <button
                  onClick={() => setIndex(i)}
                  className="flex w-[64px] shrink-0 flex-col items-center gap-1.5 sm:w-[92px]"
                >
                  <span
                    className={`flex h-8 w-8 items-center justify-center rounded-full border-2 text-[13px] font-bold transition-all ${
                      done
                        ? 'border-teal-600 bg-teal-600 text-white'
                        : active
                        ? 'border-teal-600 bg-teal-50 text-teal-700 ring-4 ring-teal-600/15'
                        : 'border-slate-300 bg-white text-slate-400'
                    }`}
                  >
                    {done ? <Check size={15} strokeWidth={3} /> : i + 1}
                  </span>
                  <span className={`text-center text-[10px] font-semibold leading-tight ${active ? 'text-teal-700' : done ? 'text-slate-700' : 'text-slate-400'}`}>
                    {s.title}
                  </span>
                </button>
              </React.Fragment>
            );
          })}
        </div>

        {/* Content */}
        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-6">
          <p className="text-[14.5px] leading-relaxed text-slate-700">{step.body}</p>
          {step.mockup}
          {step.actions?.length > 0 && (
            <div className="mt-7 border-t border-slate-100 pt-5">
              <p className="mb-3 text-[11px] font-bold uppercase tracking-wide text-slate-400">Open in dashboard</p>
              <div className="flex flex-wrap gap-2.5">
                {step.actions.map((a) => (
                  <button
                    key={a.route + a.label}
                    onClick={() => go(a.route)}
                    className="inline-flex items-center gap-1.5 rounded-xl border border-teal-200 bg-teal-50 px-4 py-2.5 text-[13px] font-semibold text-teal-700 transition-colors hover:border-teal-300 hover:bg-teal-100"
                  >
                    {a.label} <ChevronRight size={14} />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex shrink-0 items-center justify-between gap-4 border-t border-slate-100 bg-slate-50/80 px-6 py-4">
          <button
            onClick={() => setIndex((i) => Math.max(0, i - 1))}
            disabled={!canPrev}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-[13.5px] font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-40"
          >
            <ChevronLeft size={17} /> Back
          </button>
          <div className="flex gap-2">
            {STEPS.map((_, i) => (
              <button
                key={i}
                onClick={() => setIndex(i)}
                className={`h-2.5 rounded-full transition-all ${i === index ? 'w-8 bg-teal-600' : 'w-2.5 bg-slate-300 hover:bg-slate-400'}`}
                aria-label={`Go to step ${i + 1}`}
              />
            ))}
          </div>
          {canNext ? (
            <button
              onClick={() => setIndex((i) => Math.min(total - 1, i + 1))}
              className="inline-flex items-center gap-2 rounded-xl bg-teal-600 px-5 py-2.5 text-[13.5px] font-semibold text-white shadow-sm hover:bg-teal-700"
            >
              Next <ChevronRight size={17} />
            </button>
          ) : (
            <button
              onClick={onClose}
              className="inline-flex items-center gap-2 rounded-xl bg-teal-600 px-5 py-2.5 text-[13.5px] font-semibold text-white shadow-sm hover:bg-teal-700"
            >
              <Check size={16} /> Done
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default DemoTutorialModal;
